/**
Note: This implementation was built by referencing Editor AI
packages/editor/editor-plugin-ai/src/prebuilt/content-transformers/markdown-to-pm/md/media-md-plugin.ts

Aside from a refactor, the key differences in logic are:
- Our regex is more specific, and will only match images that are not inside single backticks otherwise we'll see AI Mate render images surrounded by single backticks
*/
import type MarkdownIt from 'markdown-it';
import type StateCore from 'markdown-it/lib/rules_core/state_core.mjs';
import type Token from 'markdown-it/lib/token.mjs';

function createRule() {
	// Match any images that aren't inside single backticks
	const mdImageRegex = /(?<!`)!\[[^\]]*\]\([^\)]+\)(?!`)/g;
	const validParentTokens = ['th_open', 'td_open', 'list_item_open'];

	return function media(State: StateCore) {
		const getUrl = (str: string) => {
			// Alt text can contain '(' but not ']', so the destination starts after the first ']('
			const res = State.md.helpers.parseLinkDestination(str, str.indexOf('](') + 2, str.length);
			if (res.ok) {
				const href = State.md.normalizeLink(res.str);
				if (State.md.validateLink(href)) {
					return href;
				}
			}

			return '';
		};

		const createMediaTokens = (url: string) => {
			const mediaSingleOpen = new State.Token('media_single_open', '', 1);
			const media = new State.Token('media', '', 0);
			media.attrs = [
				['url', getUrl(url)],
				['type', 'external'],
			];
			const mediaSingleClose = new State.Token('media_single_close', '', -1);

			return [mediaSingleOpen, media, mediaSingleClose];
		};

		const createInlineTokens = (str: string, openingTokens: Token[], closingTokens: Token[]) => {
			if (!str || str.length === 0) {
				return [];
			}

			const inlineBefore = new State.Token('inline', '', 1);
			inlineBefore.content = str;
			inlineBefore.children = [];

			return [...openingTokens, inlineBefore, ...closingTokens];
		};

		let processedTokens: string[] = [];
		// Track the difference between original array indices and accumulated tokens indices
		// This is needed because when we skip processed tokens, the indices diverge
		let indexOffset = 0;
		const newTokens = State.tokens.reduce(
			(tokens: Token[], token: Token, i: number, arr: Token[]) => {
				if (token.type === 'inline' && mdImageRegex.test(token.content)) {
					const openingTokens: Token[] = [];
					let cursor = i - 1;
					let previousToken = arr[cursor];
					let subTree: Token[] = [];

					// Loop backwards through the tokens until a valid parent token is found
					while (
						previousToken &&
						previousToken.level > 0 &&
						validParentTokens.indexOf(previousToken.type) === -1
					) {
						openingTokens.unshift(previousToken);
						cursor--;
						previousToken = arr[cursor];
					}

					// If the previous token is not a valid parent token, add it to the start of the opening tokens array
					if (validParentTokens.indexOf(previousToken.type) === -1) {
						openingTokens.unshift(previousToken);
					} else {
						cursor++;
					}

					// Create closing tokens for each opening token and reverse the array
					const closingTokens = openingTokens
						.map((token) => new State.Token(token.type.replace('_open', '_close'), token.tag, -1))
						.reverse();

					const matches = token.content.match(mdImageRegex)!;

					// Process all images in the inline token
					let inlineContentStack = token.content;
					matches.forEach((match) => {
						const start = inlineContentStack.indexOf(match);
						const contentBefore = inlineContentStack.substring(0, start);
						inlineContentStack = inlineContentStack.substring(start + match.length);

						// Add the inline tokens and media tokens to the subtree
						subTree = [
							...subTree,
							...createInlineTokens(contentBefore, openingTokens, closingTokens),
							...createMediaTokens(match),
						];
					});

					// If there is any remaining content in the stack, add the inline tokens to the subtree
					if (inlineContentStack.length) {
						subTree = [
							...subTree,
							...createInlineTokens(inlineContentStack, openingTokens, closingTokens),
						];
					}

					processedTokens = [...processedTokens, ...closingTokens.map((c) => c.type)];
					// Adjust cursor to account for previously skipped/modified tokens
					const adjustedCursor = cursor - indexOffset;
					tokens = [...tokens.slice(0, adjustedCursor), ...subTree];
					// Update offset: we consumed (i - cursor + 1) original tokens and added subTree.length
					indexOffset += i + 1 - cursor - subTree.length;
				} else if (processedTokens.indexOf(token.type) !== -1) {
					// Ignore token if it's already processed
					processedTokens.splice(processedTokens.indexOf(token.type), 1);
					// We skipped a token, so the offset increases
					indexOffset++;
				} else {
					tokens.push(token);
				}

				return tokens;
			},
			[],
		);

		State.tokens = newTokens;
		return true;
	};
}

export default (md: MarkdownIt): void => {
	md.core.ruler.before('inline', 'media', createRule());
};
